import styled from "styled-components";
import React, { useState, useEffect, useMemo } from "react";
import { ListContainer, Error } from "./styles";
import { useAuth } from "../AuthContext";
import axios from "axios";
import { baseUrl } from "../config";
import { apiDateToDate, formatEventDateLocal } from "../utils/dateConverters";
import { BiSolidUpvote, BiSolidDownvote } from "react-icons/bi";
import { MaterialReactTable } from "material-react-table";
import { Dialog, DialogTitle, DialogContent, List, ListItemButton, ListItemText } from "@mui/material";
import CategoryTagsCell from "./CategoryTagsCell";
import NomCellContent from "./NomCellContent";
import linkDisplayLabel from "../utils/linkDisplayLabel";

/** Maps API sortKey to MRT column id (accessorKey). */
function periodSortKeyToColumnId(key) {
  if (key === "StartDate") return "startDate";
  return key;
}

const PeriodsList = ({
  fetchPeriods,
  periods,
  sortConfig,
  setSortConfig,
  DateIRLToErache,
  rowCount,
  setPagination,
  pagination,
  setColumnFilters,
  columnFilters,
  columnVisibility,
  setColumnVisibility,
  onOpenEventDetail,
  onOpenPeriodDetail,
}) => {
  const { isLoggedIn } = useAuth();
  const [error, setError] = useState("");
  const [linksPopup, setLinksPopup] = useState(null);

  const onVote = async (periodId, inc) => {
    try {
      setError("");
      await axios.post(
        baseUrl + `/periods/${periodId}/vote`,
        { inc },
        { withCredentials: true }
      );
      fetchPeriods(pagination.pageIndex, pagination.pageSize, sortConfig?.key, sortConfig?.direction, columnFilters);
    } catch (err) {
      if (err.response?.status === 401) setError("Vous devez être connecté pour voter.");
      else if (err.response?.status === 403) setError("Vous n'avez pas le droit de voter.");
      else setError("Erreur lors du vote.");
    }
  };

  const sorting = useMemo(
    () => [{ id: periodSortKeyToColumnId(sortConfig.key), desc: sortConfig.direction === "desc" }],
    [sortConfig.key, sortConfig.direction]
  );

  const [groupedOptions, setGroupedOptions] = useState([]);
  useEffect(() => {
    axios
      .get(baseUrl + "/event-categories")
      .then((res) => {
        setGroupedOptions(
          (res.data || []).map((cat) => ({ label: cat.name, value: cat.id }))
        );
      })
      .catch(() => setError("Erreur lors de la récupération des catégories."));
  }, []);

  const columns = [
    {
      accessorKey: "startDateErache",
      header: "Début (Erache)",
      Cell: ({ row }) =>
        row.original.startDate
          ? DateIRLToErache(apiDateToDate(row.original.startDate))
          : "—",
      enableColumnFilter: false,
    },
    {
      accessorKey: "startDate",
      header: "Début (IRL)",
      size: 140,
      Cell: ({ row }) =>
        row.original.startDate
          ? formatEventDateLocal(row.original.startDate)
          : "—",
      enableColumnFilter: false,
    },
    {
      accessorKey: "endDateErache",
      header: "Fin (Erache)",
      Cell: ({ row }) =>
        row.original.endDate
          ? DateIRLToErache(apiDateToDate(row.original.endDate))
          : "En cours",
      enableColumnFilter: false,
    },
    {
      accessorKey: "endDate",
      header: "Fin (IRL)",
      size: 140,
      Cell: ({ row }) =>
        row.original.endDate
          ? formatEventDateLocal(row.original.endDate)
          : "En cours",
      enableColumnFilter: false,
    },
    {
      accessorKey: "startEventName",
      header: "Événement de départ",
      Cell: ({ row }) => {
        const id = row.original.startEventId;
        const name = row.original.startEventName || "—";
        return onOpenEventDetail && id ? (
          <NameLink type="button" onClick={(e) => { e.stopPropagation(); onOpenEventDetail(id); }}>
            {name}
          </NameLink>
        ) : (
          name
        );
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: "endEventName",
      header: "Événement de fin",
      Cell: ({ row }) => {
        const id = row.original.endEventId;
        const name = row.original.endEventName != null ? row.original.endEventName : "En cours";
        return onOpenEventDetail && id ? (
          <NameLink type="button" onClick={(e) => { e.stopPropagation(); onOpenEventDetail(id); }}>
            {name}
          </NameLink>
        ) : (
          name
        );
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: "name",
      header: "Nom",
      size: 250,
      grow: 1,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          alignItems: "flex-start",
          verticalAlign: "top",
          textOverflow: "clip",
        },
      },
      Cell: ({ row }) => <NomCellContent>{row.original.name}</NomCellContent>,
    },
    { accessorKey: "rpDescription", header: "Description RP" },
    { accessorKey: "hrpDescription", header: "Description HRP" },
    {
      accessorKey: "links",
      header: "🔗",
      size: 50,
      Cell: ({ row }) => {
        const links = row.original.links || [];
        if (links.length === 0) return "—";
        const stop = (e) => e.stopPropagation();
        if (links.length === 1) {
          return (
            <span onClick={stop}>
              <a href={links[0]} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }} onClick={stop}>
                🔗
              </a>
            </span>
          );
        }
        return (
          <span onClick={stop}>
            <LinkButton
              type="button"
              onClick={(e) => { e.stopPropagation(); setLinksPopup({ name: row.original.name, links }); }}
              title={`${links.length} liens`}
            >
              🔗
            </LinkButton>
          </span>
        );
      },
    },
    {
      accessorKey: "categories",
      grow: 1,
      size: 250,
      header: "Catégories",
      filterVariant: "multi-select",
      filterSelectOptions: groupedOptions,
        Cell: ({ cell }) => {
          const categories = cell.getValue();
          if (!categories || categories.length === 0) return "N/A";
          return <CategoryTagsCell categories={categories} />;
        },
    },
    {
      accessorKey: "votes",
      header: "Votes",
      size: 90,
      enableColumnFilter: false,
      Cell: ({ row }) =>
        isLoggedIn ? (
          <VoteContainer onClick={(e) => e.stopPropagation()}>
            <BorderlessButton onClick={() => onVote(row.original.id, 1)}>
              <BiSolidUpvote
                size={20}
                color={row.original.userVote === 1 ? "orange" : "grey"}
              />
            </BorderlessButton>
            <VoteCount>{row.original.voteCount ?? 0}</VoteCount>
            <BorderlessButton onClick={() => onVote(row.original.id, -1)}>
              <BiSolidDownvote
                size={20}
                color={row.original.userVote === -1 ? "blue" : "grey"}
              />
            </BorderlessButton>
          </VoteContainer>
        ) : null,
    },
  ];

  return (
    <PeriodsListContainer>
      {error && <Error>{error}</Error>}
      <Dialog open={!!linksPopup} onClose={() => setLinksPopup(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Liens — {linksPopup?.name}</DialogTitle>
        <DialogContent>
          <List>
            {(linksPopup?.links || []).map((url, i) => (
              <ListItemButton
                key={i}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemText
                  primary={linkDisplayLabel(url)}
                  secondary={url}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      <TableWrapper>
        <MaterialReactTable
          columns={columns}
          data={periods}
          muiTableBodyRowProps={
            onOpenPeriodDetail
              ? ({ row }) => ({
                  onClick: () => onOpenPeriodDetail(row.original.id),
                  sx: {
                    cursor: "pointer",
                    backgroundColor: "var(--bg)",
                    "&:hover": { backgroundColor: "var(--bg)" },
                  },
                })
              : undefined
          }
          enableColumnOrdering
          enableColumnResizing
          enableSorting
          enableColumnActions={false}
          enableHiding
          manualPagination
          manualFiltering
          manualSorting
          rowCount={rowCount}
          state={{ pagination, columnFilters, columnVisibility, sorting }}
          onColumnFiltersChange={setColumnFilters}
          onColumnVisibilityChange={setColumnVisibility}
          onPaginationChange={setPagination}
          onSortingChange={(updater) => {
            setSortConfig((prev) => {
              const prevSorting = [
                { id: periodSortKeyToColumnId(prev.key), desc: prev.direction === "desc" },
              ];
              const next = typeof updater === "function" ? updater(prevSorting) : updater;
              if (!next || next.length === 0) {
                return { key: "startDate", direction: "asc" };
              }
              const { id, desc } = next[0];
              return { key: id, direction: desc ? "desc" : "asc" };
            });
          }}
          muiTableContainerProps={{
            sx: { minWidth: "100%", width: "100%", backgroundColor: "var(--bg)" },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: { width: "100%", maxWidth: "100%", backgroundColor: "var(--bg)" },
          }}
          muiTableProps={{
            sx: { minWidth: "100%", width: "100%", backgroundColor: "var(--bg)" },
          }}
          initialState={{
            density: "compact",
          }}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: "var(--bg)",
              color: "var(--text-muted)",
              "& .MuiButtonBase-root": { color: "inherit" },
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              backgroundColor: "var(--bg)",
              color: "var(--text-muted)",
              "& .MuiButtonBase-root": { color: "inherit" },
            },
          }}
          muiTableBodyCellProps={{
            sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
          }}
          muiTableHeadCellProps={{
            sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
          }}
          muiTableHeadRowProps={{
            sx: { backgroundColor: "var(--bg)" },
          }}
        />
      </TableWrapper>
    </PeriodsListContainer>
  );
};

export default PeriodsList;

const NameLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-align: left;
  color: inherit;
  text-decoration: underline;
  &:hover {
    color: #1976d2;
  }
`;
const PeriodsListContainer = styled(ListContainer)`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  box-sizing: border-box;
  padding: 0 10px;
`;
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
`;
const BorderlessButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  outline: none;
`;
const VoteContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
const VoteCount = styled.span`
  min-width: 20px;
  text-align: center;
`;
const LinkButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 1em;
  &:hover {
    opacity: 0.8;
  }
`;
